import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import TrustBar from './components/TrustBar';
import FeaturesSection from './components/FeaturesSection';
import HowItWorks from './components/HowItWorks';
import WhatsAppDemoSection from './components/WhatsAppDemoSection';
import BenefitsSection from './components/BenefitsSection';
import PricingSection from './components/PricingSection';
import BlogPreview from './components/BlogPreview';
import Newsletter from './components/Newsletter';
import FinalCTA from './components/FinalCTA';
import Footer from './components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://trewaz.com';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Trewaz',
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.ico`,
  description: 'Trewaz is an AI Operating System for Travel Agencies that automates enquiries, WhatsApp communication, CRM, and booking conversions.',
  sameAs: [],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'sales',
    availableLanguage: 'English',
  },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Trewaz',
  url: SITE_URL,
  description: 'AI Operating System for Travel Agencies — automate enquiries, WhatsApp replies, CRM, and booking conversions.',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/blog?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const softwareSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Trewaz',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  url: SITE_URL,
  description: 'AI-powered platform for travel agencies to automate customer enquiries, WhatsApp communication, CRM pipeline management, and booking conversions.',
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
  },
  featureList: [
    'AI Chat Assistant',
    'WhatsApp Automation',
    'Smart CRM',
    'Booking Conversion System',
    'Lead Management',
    'Automated Follow-ups',
  ],
  provider: {
    '@type': 'Organization',
    name: 'Trewaz',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is Trewaz?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Trewaz is an AI Operating System for Travel Agencies that automates enquiries, WhatsApp communication, CRM management, and booking conversions — helping agencies close more deals without extra staff.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does Trewaz integrate with WhatsApp?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Trewaz connects directly to WhatsApp Business API and handles automated replies, booking confirmations, payment reminders, and follow-up messages on your behalf.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need coding knowledge to use Trewaz?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. Trewaz requires zero coding knowledge. Setup takes a few hours, not weeks, and is designed specifically for travel agency professionals.',
      },
    },
    {
      '@type': 'Question',
      name: 'How does Trewaz help increase booking conversions?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Trewaz responds instantly to every enquiry, captures all leads in a visual CRM pipeline, and runs automated follow-up sequences over days and weeks — ensuring no lead goes cold.',
      },
    },
  ],
};

export default function Home() {
  return (
    <div className="min-h-screen bg-white font-sans text-gray-900" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* SEO Meta Tags */}
      <title>Trewaz — AI Operating System for Travel Agencies</title>
      <meta name="description" content="Trewaz automates enquiries, WhatsApp communication, CRM, and booking conversions for travel agencies. Respond instantly, close more deals, and grow without extra staff." />
      <meta name="keywords" content="travel agency automation software, AI booking system for travel agents, WhatsApp travel automation, travel CRM software, automate travel enquiries, AI travel agency, travel agency software UK" />
      <meta name="author" content="Trewaz" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={SITE_URL} />
      <meta name="last-modified" content="2026-03-29" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Trewaz" />
      <meta property="og:title" content="Trewaz — AI Operating System for Travel Agencies" />
      <meta property="og:description" content="Automate enquiries, WhatsApp replies, CRM, and booking conversions. Built for travel agencies. No coding required." />
      <meta property="og:url" content={SITE_URL} />
      <meta property="og:image" content="https://public.readdy.ai/ai/img_res/e2e83ca36577a8e7f1df2a4c107dff3f.jpg" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Trewaz — AI Operating System for Travel Agencies" />
      <meta name="twitter:description" content="Automate enquiries, WhatsApp replies, CRM, and booking conversions. Built for travel agencies." />
      <meta name="twitter:image" content="https://readdy.ai/api/search-image?query=modern%20minimalist%20SaaS%20dashboard%20for%20travel%20agency%20AI%20automation%20clean%20white%20interface%20professional%20B2B%20software%20analytics&width=1200&height=630&seq=oghome&orientation=landscape" />

      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Google Fonts - Inter */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');`}</style>

      <Navbar />
      <main>
        <HeroSection />
        <TrustBar />
        <FeaturesSection />
        <HowItWorks />
        <WhatsAppDemoSection />
        <BenefitsSection />
        <PricingSection />
        <BlogPreview />
        <Newsletter />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
